import React, { useEffect, useState } from 'react';
import TabBar from '../../../component/tab-bar/tab-bar';
import ProgramCoursesTab from './courses/program-courses-tab';
import ProgramStudentsTab from './students/program-students-tab';
import ProgramServicesTab from './payment-services/program-services-tab';
import globalProgramStore from '../../../stores/program-store';
import { SplitButton } from 'primereact/splitbutton';
import {
  ProgramType,
  ProgramWithDetails,
  ProgramYearsDistribution,
} from '../../../api/types/programs/program.type';
import { ProgramStatus } from '../../../api/types/programs/program-status';
import { updateProgramStatusOperation } from './operations/update-program-status-operation';
import { updateProgramActionNumberOperation } from './operations/update-program-action-number-operation';
import { updateProgramNotesOperation } from './operations/update-program-notes-operation';
import { ProgramFinancialStatusOverview } from './financial/program-financial-status-overview';
import globalConfigStore from '../../../stores/global-config-store';
import { toast } from 'react-toastify';
import appApi from '../../../api/app-api';
import { CourseStatus } from '../../../api/types/programs/course/course-status';
import globalCourseStore from '../../../stores/global-course-store';
import { observer } from 'mobx-react-lite';
import { useParams } from 'react-router-dom';
import { getHebrewYear } from '../../../utils/hebrew-date-utils';
import { Tooltip } from 'primereact/tooltip';
import { EditActionNumberDialog } from './dialogs/edit-action-number-dialog';
import { EditNotesDialog } from './dialogs/edit-notes-dialog';
import { ProgramPageExternalExamsTab } from './external-exams/program-page-external-exams-tab';

const tabs = [
  'קורסים',
  'שרותי תשלום',
  'סטודנטים',
  'מבחנים חיצוניים',
  'שיקוף מצב פיננסי' /*, 'מבחנים חיצוניים'*/,
];

const severityMap = {
  [ProgramStatus.INIT]: 'secondary',
  [ProgramStatus.ACTIVE]: 'info',
  [ProgramStatus.FINISHED]: 'success',
  [ProgramStatus.CLOSE]: 'danger',
};
const ProgramPage = observer(() => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [loadingUpdateStatus, setLoadingUpdateStatus] = useState(false);
  const [showActionNumberDialog, setShowActionNumberDialog] = useState(false);
  const [actionNumberInput, setActionNumberInput] = useState('');
  const [loadingActionNumber, setLoadingActionNumber] = useState(false);
  const [showNotesDialog, setShowNotesDialog] = useState(false);
  const [notesInput, setNotesInput] = useState('');
  const [loadingNotes, setLoadingNotes] = useState(false);

  const { programId } = useParams();

  const pages = [
    <ProgramCoursesTab />,
    <ProgramServicesTab />,
    <ProgramStudentsTab />,
    <ProgramPageExternalExamsTab />,
    <div style={{ display: 'flex', flexDirection: 'row', width: '100%' }}>
      <ProgramFinancialStatusOverview />
    </div>,
  ];

  const getRowPageHeadlineDetails = (title: string, value: string) => {
    return (
      <div
        style={{ display: 'flex', flexDirection: 'row', marginLeft: '20px' }}
      >
        <div style={{ color: '#6A6A6A' }}>{title}</div>
        <div
          style={{ color: '#404040', marginRight: '8px', fontWeight: 'bold' }}
        >
          {value}
        </div>
      </div>
    );
  };

  const [currentProgram, setCurrentProgram] = useState<
    ProgramWithDetails | undefined
  >();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const programIdNum = Number(programId);

    const onCall = async () => {
      if (!globalConfigStore.isReady) {
        return;
      }

      if (!loading && programIdNum) {
        setLoading(true);
        try {
          const branchId = globalConfigStore.rootFilter?.currentBranches?.id;
          if (!branchId) {
            toast.warn('יש לבחור את הסניף המבוקש');
            return;
          }

          globalProgramStore.currentId = programIdNum;
          const programDetailsRes = await appApi.getProgramDetails(
            branchId,
            programIdNum,
          );

          const programData = programDetailsRes.data
            .body as ProgramYearsDistribution;

          // Store the program data
          globalProgramStore.programs.set(programIdNum, programData);
          globalProgramStore.currentId = programIdNum;

          // Update course statuses in all years
          if (programData.details?.years) {
            programData.details.years.forEach((yearData) => {
              if (yearData.courses) {
                yearData.courses = yearData.courses.map((course) => ({
                  ...course,
                  status: CourseStatus.parseCourseStatus(course.status),
                }));
              }
            });
          }

          // Add courses to the course store
          const allCourses = [
            ...(programData.details?.unAssignCourses || []),
            ...(programData.details?.years?.flatMap((y) => y.courses) || []),
          ];

          if (allCourses.length > 0) {
            globalCourseStore.addCourseMetadataList(allCourses);
          }

          setCurrentProgram(programData);
          toast.info('טעינת התוכנית התרחשה בהצלחה');
        } catch (e) {
          console.error(`Loading program details failed with the error: ${e}`);
          toast.error('טעינת התוכנית נכשלה, נסה שוב');
        } finally {
          setLoading(false);
        }
      }
    };

    onCall();
  }, [globalConfigStore.isReady]);

  //const programStatus2 = (currentProgram as ProgramType).metadata.status;
  const programStatus = currentProgram?.metadata.status;
  const statusDisplayText = ProgramStatus.getDisplayText(programStatus);
  //const programStatus = ProgramStatus.CLOSE;

  const [severity, setSeverity] = useState<
    | 'secondary'
    | 'success'
    | 'info'
    | 'warning'
    | 'danger'
    | 'contrast'
    | undefined
  >();

  useEffect(() => {
    // Update severity based on the current program status using a map for cleaner code
    const newSeverity = severityMap[programStatus];
    setSeverity(newSeverity);
  }, [programStatus]);

  const handleUpdateStatus = async (status: ProgramStatus) => {
    if (!currentProgram) return;

    await updateProgramStatusOperation(
      currentProgram.metadata.branchId,
      currentProgram.metadata.id,
      status,
      setLoadingUpdateStatus,
      currentProgram as ProgramType,
      ((updatedProgram: ProgramType) =>
        setCurrentProgram(updatedProgram as ProgramWithDetails)) as (
        program: ProgramType,
      ) => any,
    );
  };

  const handleUpdateActionNumber = async () => {
    if (!currentProgram) return;

    await updateProgramActionNumberOperation(
      currentProgram.metadata.branchId,
      currentProgram.metadata.id,
      actionNumberInput,
      setLoadingActionNumber,
      currentProgram as ProgramType,
      ((updatedProgram: ProgramType) =>
        setCurrentProgram(updatedProgram as ProgramWithDetails)) as (
        program: ProgramType,
      ) => any,
    );
    setShowActionNumberDialog(false);
  };

  const handleUpdateNotes = async () => {
    if (!currentProgram) return;

    await updateProgramNotesOperation(
      currentProgram.metadata.branchId,
      currentProgram.metadata.id,
      notesInput,
      setLoadingNotes,
      currentProgram as ProgramType,
      ((updatedProgram: ProgramType) =>
        setCurrentProgram(updatedProgram as ProgramWithDetails)) as (
        program: ProgramType,
      ) => any,
    );
    setShowNotesDialog(false);
  };

  const items = [
    {
      label: 'הפעל',
      command: async () => handleUpdateStatus(ProgramStatus.ACTIVE),
    },
    {
      label: 'סיום התוכנית',
      command: async () => handleUpdateStatus(ProgramStatus.FINISHED),
    },
    {
      label: 'השהיית התוכנית',
      command: async () => handleUpdateStatus(ProgramStatus.CLOSE),
    },
  ];

  return (
    <div
      style={{
        marginTop: '16px',
      }}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center', // This aligns children vertically in the center
          marginTop: '8px',
        }}
      >
        <div
          style={{
            color: '#041E49',
            fontSize: '30px',
            fontWeight: 'bold',
          }}
        >
          {currentProgram?.metadata?.programName ?? ''}
        </div>
        <SplitButton
          loading={loadingUpdateStatus}
          label={statusDisplayText}
          style={{ height: '30px', direction: 'ltr', marginRight: '8px' }}
          onClick={() => {
            //console.log('XX');
          }}
          onChange={(data) => {
            //console.log(`xx ` + data);
          }}
          model={items}
          severity={severity}
          rounded
        />
      </div>
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          marginTop: '10px',
          marginBottom: '20px',
        }}
      >
        {getRowPageHeadlineDetails(
          'שנת התחלה:',
          currentProgram?.metadata?.programStartYear
            ? `${getHebrewYear(
                currentProgram?.metadata?.programStartYear,
              ).toString()} (${currentProgram?.metadata?.programStartYear.toString()})`
            : 'טרם התחיל',
        )}
        {getRowPageHeadlineDetails(
          'מספר סידורי:',
          currentProgram?.metadata?.id.toString() ?? '',
        )}
        {getRowPageHeadlineDetails(
          'שנות לימוד:',
          currentProgram?.metadata?.years.toString() ?? '',
        )}
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
          }}
        >
          {getRowPageHeadlineDetails(
            'קוד פעולה:',
            currentProgram?.metadata?.actionNumber || 'לא הוגדר',
          )}
          <i
            className="pi pi-pencil edit-action-number-btn"
            style={{
              fontSize: '14px',
              color: '#6A6A6A',
              cursor: 'pointer',
              marginRight: '8px',
            }}
            onClick={() => {
              setActionNumberInput(
                currentProgram?.metadata?.actionNumber || '',
              );
              setShowActionNumberDialog(true);
            }}
          />
          <Tooltip target=".edit-action-number-btn" content="עריכת קוד פעולה" />
        </div>
      </div>

      {/* Notes Section */}
      <div
        style={{
          marginTop: '16px',
          marginBottom: '16px',
          padding: '12px',
          backgroundColor: '#f8f9fa',
          borderRadius: '8px',
          border: '1px solid #e0e0e0',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
          }}
        >
          <div style={{ flex: 1 }}>
            <div
              style={{
                color: '#6A6A6A',
                fontSize: '14px',
                fontWeight: 'bold',
                marginBottom: '8px',
              }}
            >
              הערות:
            </div>
            <div
              style={{
                color: '#404040',
                fontSize: '14px',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {currentProgram?.metadata?.notes || 'אין הערות'}
            </div>
          </div>
          <div>
            <i
              className="pi pi-pencil edit-notes-btn"
              style={{
                fontSize: '14px',
                color: '#6A6A6A',
                cursor: 'pointer',
                marginRight: '8px',
              }}
              onClick={() => {
                setNotesInput(currentProgram?.metadata?.notes || '');
                setShowNotesDialog(true);
              }}
            />
            <Tooltip target=".edit-notes-btn" content="עריכת הערות" />
          </div>
        </div>
      </div>

      <TabBar
        tabs={tabs}
        onClick={(index) => {
          setSelectedIndex(index);
        }}
      ></TabBar>

      <br />

      {pages[selectedIndex]}

      <EditActionNumberDialog
        visible={showActionNumberDialog}
        onHide={() => setShowActionNumberDialog(false)}
        actionNumber={actionNumberInput}
        onActionNumberChange={setActionNumberInput}
        onApprove={handleUpdateActionNumber}
        loading={loadingActionNumber}
      />

      <EditNotesDialog
        visible={showNotesDialog}
        onHide={() => setShowNotesDialog(false)}
        notes={notesInput}
        onNotesChange={setNotesInput}
        onApprove={handleUpdateNotes}
        loading={loadingNotes}
      />
    </div>
  );
});

export default ProgramPage;
